import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';

export interface GroupMember {
  id: string;
  externalId: string;
  name: string;
  publicationStatus: string;
  isBlocked: boolean;
  blockedReason: string | null;
}

export interface DuplicateGroup {
  groupKey: string;
  parent: GroupMember | null;
  members: GroupMember[];
  memberCount: number;
}

interface GroupActionBody {
  partnerAccountId: number;
  groupKey: string;
  syncedLocationIds: number[];
}

// Fetch duplicate groups for a partner account
async function fetchGroups(partnerAccountId: number | null): Promise<DuplicateGroup[]> {
  const response = await apiClient.get<{ data: DuplicateGroup[] }>(
    `/api/v1/groups?partnerAccountId=${String(partnerAccountId)}`
  );
  return response.data;
}

// Ignore (hide) the given units within a group
async function ignoreUnits(body: GroupActionBody): Promise<{ operationId: string }> {
  const response = await apiClient.post<{ data: { operationId: string } }>(
    '/api/v1/groups/ignore',
    body
  );
  return response.data;
}

// Restore previously ignored units within a group
async function unignoreUnits(body: GroupActionBody): Promise<{ unignored: number }> {
  const response = await apiClient.post<{ data: { unignored: number } }>(
    '/api/v1/groups/unignore',
    body
  );
  return response.data;
}

// Query keys
export const groupKeys = {
  all: ['groups'] as const,
  list: (partnerAccountId: number | null) => [...groupKeys.all, partnerAccountId] as const,
};

// Hooks
export function useGroups(partnerAccountId: number | null) {
  return useQuery({
    queryKey: groupKeys.list(partnerAccountId),
    enabled: partnerAccountId != null,
    queryFn: () => fetchGroups(partnerAccountId),
  });
}

export function useIgnoreUnits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: GroupActionBody) => ignoreUnits(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useUnignoreUnits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: GroupActionBody) => unignoreUnits(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}
